import { z } from 'zod';

/**
 * ERP-Connector (Modul „erp“): Business Central ↔ Shopify – Plan und Abgleich mit der Doku in docs/erp-connector.md.
 * Business Central ist der erste Adapter; weitere ERPs (JTL-Wawi, Xentral …) brauchen nur einen neuen Adapter.
 */

/** Systeme mit Adapter – nur diese lassen sich verbinden (Check-Constraint erp_verbindung.system wächst mit) */
export const ERP_SYSTEME = ['business_central'] as const;
export type ErpSystem = (typeof ERP_SYSTEME)[number];
export const ERP_SYSTEM_TITEL: Record<ErpSystem, string> = { business_central: 'Microsoft Dynamics 365 Business Central' };
export const istErpSystem = (s: string): s is ErpSystem => (ERP_SYSTEME as readonly string[]).includes(s);

/**
 * Auswahl „ERP verbinden“. Ein Mandant kann je ERP eine Verbindung haben (erp_verbindung eindeutig je Mandant und
 * System). Weitere ERPs brauchen nur einen Adapter nach dem ErpAdapter-Interface – bis dahin „demnächst“.
 */
export const ERP_KATALOG = [
  { id: 'business_central', kuerzel: 'BC', name: 'Microsoft Dynamics 365 Business Central', text: 'Business Central online (SaaS): Artikel, Bestand, Preise, Aufträge, Versand und Retouren – mit oder ohne Extension.', aktiv: true },
  { id: 'jtl_wawi', kuerzel: 'JW', name: 'JTL-Wawi', text: 'Warenwirtschaft für Händler im deutschsprachigen Raum.', aktiv: false },
  { id: 'xentral', kuerzel: 'XE', name: 'Xentral', text: 'Cloud-ERP für wachsende Onlinehändler.', aktiv: false },
  { id: 'sap_b1', kuerzel: 'B1', name: 'SAP Business One', text: 'ERP für kleine und mittlere Unternehmen (Service Layer).', aktiv: false },
] as const satisfies readonly { id: string; kuerzel: string; name: string; text: string; aktiv: boolean }[];
export type ErpKatalogEintrag = (typeof ERP_KATALOG)[number];
export const erpKatalog = (id: string) => ERP_KATALOG.find((e) => e.id === id);

/** GET /api/konto/erp – Übersicht der verbundenen ERPs */
export interface ErpVerbindungKurz {
  system: ErpSystem;
  umgebung: string;
  companyName: string;
  testmodus: boolean;
  geprueftAm: string | null;
  pruefFehler: string;
  /** aufgegebene Aufträge (Dead-Letter) */
  tot: number;
}
export interface ErpSystemListe {
  verbindungen: ErpVerbindungKurz[];
}

/** Reiter der Seite „ERP-Connector“ (Briefing A4) – Inhalte von Produkte bis Retouren folgen mit den Teilen B–D */
export const ERP_REITER = [
  { id: 'verbindung', titel: 'Verbindung', text: 'Zugang zu Business Central, Company und Berechtigungen im Shop.' },
  { id: 'produkte', titel: 'Produkte & Felder', text: 'Welche Artikel in den Shop gehen und wer welches Feld führt.' },
  { id: 'lagerorte', titel: 'Lagerorte', text: 'Lagerorte in Business Central und Standorte im Shop.' },
  { id: 'preise', titel: 'Preise & Steuern', text: 'Verkaufs- und Streichpreis, Brutto und Netto.' },
  { id: 'bestellungen', titel: 'Bestellungen', text: 'Wann und wie Bestellungen aus dem Shop als Verkaufsauftrag in Business Central angelegt werden.' },
  { id: 'versand', titel: 'Versand', text: 'Gebuchte Lieferungen aus Business Central als Versand mit Sendungsnummer im Shop.' },
  { id: 'retouren', titel: 'Retouren', text: 'Reklamationen, Wareneingang und Erstattungen zwischen Retourenmanagement, Business Central und Shop.' },
  { id: 'protokoll', titel: 'Protokoll', text: 'Jeder Abgleich je Objekt: Richtung, Zeitpunkt, Ergebnis.' },
  { id: 'fehler', titel: 'Fehler', text: 'Aufträge, die nach allen Wiederholungen nicht geklappt haben – prüfen und erneut auslösen.' },
] as const;
export type ErpReiter = (typeof ERP_REITER)[number]['id'];
export const ERP_REITER_IDS = ERP_REITER.map((r) => r.id) as [ErpReiter, ...ErpReiter[]];

/* ---------------------------------------------------------------- Verbindung (Teil A2) */

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const Guid = (meldung: string) => z.string().trim().regex(GUID, meldung).transform((s) => s.toLowerCase());

export const ErpVerbindungSchema = z.object({
  /** Entra-Tenant: GUID oder Domain (beispiel.onmicrosoft.com) */
  tenantId: z.string().trim().min(1, 'Bitte die Tenant-ID angeben')
    .refine((s) => GUID.test(s) || /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(s), 'Bitte die Tenant-ID (GUID) oder die Domain des Tenants angeben')
    .transform((s) => s.toLowerCase()),
  clientId: Guid('Bitte die Anwendungs-ID (Client-ID) der Entra-App angeben (GUID)'),
  /** leer = bisheriges Secret behalten */
  clientSecret: z.string().trim().max(200, 'Das Secret ist zu lang').default(''),
  /** Name der BC-Umgebung, z. B. Sandbox oder Production */
  umgebung: z.string().trim().regex(/^[A-Za-z0-9_-]{1,30}$/, 'Bitte den Namen der Umgebung angeben (z. B. Sandbox oder Production)'),
});
export type ErpVerbindungEingabe = z.input<typeof ErpVerbindungSchema>;

export const ErpCompanySchema = z.object({ companyId: Guid('Bitte eine Company wählen') });

export interface ErpCompany {
  id: string;
  name: string;
  anzeige: string;
}

/** Custom-API „DXL Commerce Connector API“ (Teil E) – null = noch nicht geprüft */
export interface ErpExtension {
  installiert: boolean;
  version: string | null;
}

/**
 * Was die Extension freischaltet – und welcher Fallback ohne sie gilt (Teil E, Briefing B2/B3/C1/C3/D).
 * Die Sync-Schritte fragen erpFaehigkeiten() statt die Extension direkt.
 */
export const ERP_FAEHIGKEITEN = [
  { id: 'bestandJeLagerort', titel: 'Bestand je Lagerort', mit: 'inventoryByLocation', ohne: 'Gesamtbestand des Artikels (items.inventory)' },
  { id: 'offeneMengen', titel: 'Offene Auftragsmengen ohne Shop-Bestellungen', mit: 'openSalesQuantities', ohne: 'nicht abgezogen – nur Sicherheitsbestand' },
  { id: 'artikeldetails', titel: 'Gewicht, Hersteller, Verkaufssperre', mit: 'itemDetails', ohne: 'nur Felder der Standard-API' },
  { id: 'attribute', titel: 'Artikelattribute', mit: 'itemAttributes', ohne: 'Optionen nur aus Variantencode und -beschreibung' },
  { id: 'preislisten', titel: 'Preise aus Preislisten', mit: 'salesPrices', ohne: 'Verkaufspreis des Artikels, kein Streichpreis aus Preisliste' },
  { id: 'auftragExtras', titel: 'Zahlungsform und Zusteller am Auftrag', mit: 'salesOrderExtras', ohne: 'Vorgaben des Debitors' },
  { id: 'sendungsnummer', titel: 'Sendungsnummer und Zusteller', mit: 'shipmentTracking', ohne: 'Versand ohne Sendungsnummer' },
  { id: 'reklamationen', titel: 'Reklamationen und Retouren-Wareneingang', mit: 'salesReturnOrders, returnReceipts', ohne: 'nicht möglich – Retouren nur als Gutschrift' },
] as const;
export type ErpFaehigkeit = (typeof ERP_FAEHIGKEITEN)[number]['id'];

/** Mit installierter Extension alles, ohne (oder unbekannt) nur die Fallbacks */
export const erpFaehigkeiten = (e: ErpExtension | null): Record<ErpFaehigkeit, boolean> =>
  Object.fromEntries(ERP_FAEHIGKEITEN.map((f) => [f.id, !!e?.installiert])) as Record<ErpFaehigkeit, boolean>;

export interface ErpVerbindungAnzeige {
  system: ErpSystem;
  tenantId: string;
  clientId: string;
  umgebung: string;
  /** das Secret selbst kommt nie zurück */
  secretGesetzt: boolean;
  companyId: string | null;
  companyName: string;
  geprueftAm: string | null;
  pruefFehler: string;
  extension: ErpExtension | null;
  testmodus: boolean;
  voraussetzungen: ErpVoraussetzung[];
  geaendertAm: string;
}

export interface ErpTestErgebnis {
  ok: boolean;
  text: string;
  companies: ErpCompany[];
  extension: ErpExtension | null;
}

/* ---------------------------------------------------------------- Shopify-Seite (Teil A3) */

/**
 * Berechtigungen im Shop, die der ERP-Connector braucht – eigene Liste, damit Marktplätze ohne ERP keine Warnung sehen.
 * Die App „DXL Marketplace“ bietet sie als optionale Scopes an; Retouren kommen mit dem nächsten Stand der App.
 */
export const ERP_BEREICHE = [
  { bereich: 'read_products', text: 'Produkte lesen', ab: 'B' },
  { bereich: 'write_products', text: 'Produkte anlegen und ändern', ab: 'B' },
  { bereich: 'read_inventory', text: 'Bestand lesen', ab: 'B' },
  { bereich: 'write_inventory', text: 'Bestand setzen', ab: 'B' },
  { bereich: 'read_locations', text: 'Standorte lesen', ab: 'B' },
  { bereich: 'write_publications', text: 'Im Onlineshop veröffentlichen', ab: 'B' },
  { bereich: 'read_orders', text: 'Bestellungen lesen', ab: 'C' },
  { bereich: 'write_orders', text: 'Bestellungen markieren und erstatten', ab: 'C' },
  { bereich: 'write_merchant_managed_fulfillment_orders', text: 'Versand melden', ab: 'C' },
  { bereich: 'read_returns', text: 'Retouren lesen', ab: 'D' },
  { bereich: 'write_returns', text: 'Retouren abschließen', ab: 'D' },
] as const;
export type ErpBereich = (typeof ERP_BEREICHE)[number]['bereich'];

/** Fehlende Berechtigungen – write_x deckt read_x ab (wie shopBereicheFehlend) */
export function erpBereicheFehlend(erteilt: readonly string[]): ErpBereich[] {
  const hat = new Set(erteilt.map((b) => b.trim()).filter(Boolean));
  return ERP_BEREICHE.map((b) => b.bereich).filter((b) => !hat.has(b) && !(b.startsWith('read_') && hat.has(`write_${b.slice(5)}`)));
}

export interface ErpShopStand {
  verbunden: boolean;
  shop: string | null;
  fehlend: ErpBereich[];
  /** Zeitpunkt, zu dem die erteilten Berechtigungen zuletzt von der App kamen */
  stand: string | null;
}

/* ---------------------------------------------------------------- Datenhoheit (Briefing 1.2) */

export const ERP_HOHEITEN = ['erp', 'shopify'] as const;
export type ErpHoheit = (typeof ERP_HOHEITEN)[number];

export const ERP_FELDER = [
  { id: 'titel', bereich: 'Artikelstamm', titel: 'Titel', standard: 'erp' },
  { id: 'marke', bereich: 'Artikelstamm', titel: 'Marke (Hersteller)', standard: 'erp' },
  { id: 'varianten', bereich: 'Artikelstamm', titel: 'Varianten und Optionen', standard: 'erp' },
  { id: 'sku', bereich: 'Artikelstamm', titel: 'SKU', standard: 'erp' },
  { id: 'gtin', bereich: 'Artikelstamm', titel: 'GTIN / EAN', standard: 'erp' },
  { id: 'gewicht', bereich: 'Artikelstamm', titel: 'Gewicht', standard: 'erp' },
  { id: 'kategorie', bereich: 'Artikelstamm', titel: 'Produktart / Kategorie', standard: 'erp' },
  { id: 'bestand', bereich: 'Bestand & Preis', titel: 'Bestand', standard: 'erp' },
  { id: 'preis', bereich: 'Bestand & Preis', titel: 'Preis', standard: 'erp' },
  { id: 'streichpreis', bereich: 'Bestand & Preis', titel: 'Streichpreis', standard: 'erp' },
  { id: 'beschreibung', bereich: 'Inhalte', titel: 'Beschreibung', standard: 'shopify' },
  { id: 'seo', bereich: 'Inhalte', titel: 'SEO-Titel und -Beschreibung', standard: 'shopify' },
  { id: 'bilder', bereich: 'Inhalte', titel: 'Bilder', standard: 'shopify' },
  { id: 'kollektionen', bereich: 'Inhalte', titel: 'Kollektionen', standard: 'shopify' },
] as const satisfies readonly { id: string; bereich: string; titel: string; standard: ErpHoheit }[];
export type ErpFeld = (typeof ERP_FELDER)[number]['id'];
export const ERP_FELD_IDS = ERP_FELDER.map((f) => f.id) as [ErpFeld, ...ErpFeld[]];

/** Feste Richtungen (nicht umstellbar, nur zur Anzeige) */
export const ERP_RICHTUNGEN = [
  { titel: 'Bestellungen', text: 'Shop → Business Central' },
  { titel: 'Versand und Sendungsnummer', text: 'Business Central → Shop' },
  { titel: 'Retouren-Wareneingang', text: 'Business Central führt' },
  { titel: 'Erstattung', text: 'DXL Commerce löst sie im Shop aus' },
] as const;

export type ErpFeldhoheit = Record<ErpFeld, ErpHoheit>;
export const erpFeldhoheitStandard = (): ErpFeldhoheit => Object.fromEntries(ERP_FELDER.map((f) => [f.id, f.standard])) as ErpFeldhoheit;
export const ErpFeldhoheitSchema = z.object({ felder: z.record(z.enum(ERP_FELD_IDS), z.enum(ERP_HOHEITEN)) });

/* ---------------------------------------------------------------- Teil B: Produkte, Lagerorte, Preise */

/** Metafeld-Namespace des ERP-Connectors – markiert eigene Produkte (dxl_erp.ref = Artikel-ID im ERP) */
export const ERP_METAFELD_NS = 'dxl_erp';

const Code = z.string().trim().max(20);

/** Veröffentlichen per Regel: neu angelegte Entwürfe werden aktiviert, sobald die Bedingung im Shop erfüllt ist */
export const ERP_FREIGABE_REGELN = ['keine', 'bild', 'bild_text'] as const;
export type ErpFreigabeRegel = (typeof ERP_FREIGABE_REGELN)[number];
export const ERP_FREIGABE_TITEL: Record<ErpFreigabeRegel, string> = {
  keine: 'nicht automatisch',
  bild: 'mit Bild',
  bild_text: 'mit Bild und Text',
};

export const ErpProduktEinstellungenSchema = z.object({
  /** SKU im Shop: Platzhalter {itemNo} und {variantCode} */
  skuRegel: z.string().trim().max(60).regex(/\{itemNo\}/, 'Die Regel braucht {itemNo}').default('{itemNo}-{variantCode}'),
  /** Option der Varianten im Shop (Name) und woher der Wert kommt */
  optionName: z.string().trim().min(1).max(40).default('Variante'),
  optionWert: z.enum(['beschreibung', 'code']).default('beschreibung'),
  /** Marke (vendor): Hersteller- oder Kreditorcode bzw. ein Artikelattribut (Extension) */
  marke: z.enum(['hersteller', 'kreditor', 'attribut', 'keine']).default('hersteller'),
  markeAttribut: z.string().trim().max(60).default(''),
  /** Produktart: Artikelkategorie aus dem ERP */
  kategorie: z.enum(['artikelkategorie', 'keine']).default('artikelkategorie'),
  /** Welche Artikel in den Shop gehen: Kategorien (leer = alle), nur Lagerartikel, Webshop-Attribut (Extension) */
  filterKategorien: z.array(Code).max(200).default([]),
  nurLagerartikel: z.boolean().default(true),
  webshopAttribut: z.string().trim().max(60).default(''),
  webshopWert: z.string().trim().max(60).default(''),
  /** Status neuer Produkte im Shop */
  neuStatus: z.enum(['DRAFT', 'ACTIVE']).default('DRAFT'),
  /** Entwürfe automatisch aktivieren, sobald die Regel erfüllt ist (nur neu angelegte Produkte) */
  freigabe: z.enum(ERP_FREIGABE_REGELN).default('keine'),
  /** Bild aus dem ERP, wenn das Produkt im Shop noch kein Bild hat */
  bildAusErp: z.boolean().default(true),
  /** vorhandene eigene Shop-Produkte mit gleicher SKU übernehmen (nie Händler- oder Buy-Box-Varianten) */
  vorhandeneUebernehmen: z.boolean().default(true),
  /** Artikelattribute als Metafelder im Namespace dxl_erp (Extension) */
  metafelder: z.array(z.object({ attribut: z.string().trim().min(1).max(60), key: z.string().regex(/^[a-z][a-z0-9_]{0,29}$/, 'Schlüssel: Kleinbuchstaben, Ziffern, _') })).max(30).default([]),
  /** Takt: Delta-Abgleich alle n Minuten, Vollabgleich nachts zur Stunde (Europe/Berlin) */
  deltaMinuten: z.number().int().min(1).max(60).default(5),
  vollStunde: z.number().int().min(0).max(23).default(3),
});
export type ErpProduktEinstellungen = z.infer<typeof ErpProduktEinstellungenSchema>;

export const ErpLagerEinstellungenSchema = z.object({
  /** BC-Lagerort → Shopify-Location (Ziffern der ID); Phase 1 eine Zeile, gebaut für mehrere */
  zuordnung: z.array(z.object({ lagerort: Code.min(1, 'Bitte einen Lagerort wählen'), location: z.string().regex(/^\d+$/, 'Bitte einen Standort wählen') })).max(20).default([]),
  sicherheitsbestand: z.number().int().min(0).max(100000).default(0),
});
export type ErpLagerEinstellungen = z.infer<typeof ErpLagerEinstellungenSchema>;

export const ErpPreisEinstellungenSchema = z.object({
  /** Verkaufspreis: Artikel (unitPrice) oder Preisliste (Extension) */
  quelle: z.enum(['artikel', 'preisliste']).default('artikel'),
  preisliste: Code.default(''),
  /** Streichpreis aus einer Preisliste (Extension) – leer = keiner */
  streichpreisListe: Code.default(''),
  /** Steuersatz für die Umrechnung Brutto ↔ Netto (Phase 1: DE 19 %) */
  steuersatz: z.number().min(0).max(30).default(19),
});
export type ErpPreisEinstellungen = z.infer<typeof ErpPreisEinstellungenSchema>;

export interface ErpStromStand {
  strom: string;
  letzterLauf: string | null;
  naechsterLauf: string | null;
  naechsterVoll: string | null;
  fehler: string;
  bericht: Record<string, number | string>;
}

export interface ErpBulkStand {
  id: string;
  zustand: 'wartet' | 'laeuft' | 'fertig' | 'fehler';
  anzahl: number;
  erledigt: number;
  fehler: number;
  text: string;
  erstelltAm: string;
  geaendertAm: string;
}

export interface ErpLagerorte {
  erp: { code: string; name: string }[];
  shop: { id: string; name: string; aktiv: boolean }[];
  /** Fehlertext, wenn eine Seite nicht lesbar war */
  fehler: string;
}

export const ErpAbgleichSchema = z.object({ art: z.enum(['artikel', 'bestand']), voll: z.boolean().default(false) });

/* ---------------------------------------------------------------- Voraussetzungen vom Mandanten (Checkliste) */

export const ERP_VORAUSSETZUNGEN = [
  { id: 'sandbox', titel: 'Sandbox mit aktuellen Stammdaten', text: 'Eine Business-Central-Sandbox zum Testen, möglichst mit einer Kopie der Produktivdaten.' },
  { id: 'entra', titel: 'Entra-App freigegeben', text: 'App-Registrierung (Tenant-ID, Client-ID, Secret), in Business Central als Microsoft Entra-Anwendung angelegt und freigegeben.' },
  { id: 'felder', titel: 'Felder für Marke, Farbe, Größe, Material, Kategorie, GTIN', text: 'In welchen Feldern oder Attributen von Business Central diese Angaben stehen.' },
  { id: 'webshop', titel: 'Kriterium „geht in den Webshop“', text: 'Woran erkennbar ist, dass ein Artikel im Shop erscheinen soll (Kennzeichen, Artikelkategorie, nicht gesperrt).' },
  { id: 'lager', titel: 'Lagerort-Codes', text: 'Welche Lagerorte für Phase 1 in den Shop-Bestand eingehen.' },
  { id: 'codes', titel: 'Zahlungs-, Zusteller- und Versandcodes', text: 'Zahlungsform-Codes, Zusteller- und Versandcodes, Artikel oder Sachkonto für Versandkosten.' },
  { id: 'debitor', titel: 'Sammeldebitor oder Einzeldebitoren', text: 'Ob Bestellungen auf einen Sammeldebitor je Land oder auf einen Debitor je Kunde gehen.' },
  { id: 'preislisten', titel: 'Preislisten', text: 'Preisliste(n) für Verkaufs- und Streichpreis.' },
  { id: 'retouren', titel: 'Retourenprozess im Lager', text: 'Wer bucht den Wareneingang, wann wird erstattet.' },
] as const;
export type ErpVoraussetzung = (typeof ERP_VORAUSSETZUNGEN)[number]['id'];
export const ERP_VORAUSSETZUNG_IDS = ERP_VORAUSSETZUNGEN.map((v) => v.id) as [ErpVoraussetzung, ...ErpVoraussetzung[]];

export const ErpEinstellungenSchema = z.object({
  /** Testmodus (Dry-Run): zeigt, was geschrieben würde, ohne zu schreiben */
  testmodus: z.boolean(),
  voraussetzungen: z.array(z.enum(ERP_VORAUSSETZUNG_IDS)).max(ERP_VORAUSSETZUNGEN.length),
});
export type ErpEinstellungenEingabe = z.input<typeof ErpEinstellungenSchema>;

/* ---------------------------------------------------------------- Aufträge, Protokoll, Fehler (Briefing 1.4) */

/** offen → laeuft → fertig | fehler (nächster Versuch geplant) | tot (alle Versuche verbraucht = Dead-Letter) */
export const SYNC_STATUS = ['offen', 'laeuft', 'fertig', 'fehler', 'tot'] as const;
export type SyncStatus = (typeof SYNC_STATUS)[number];
export const SYNC_STATUS_TITEL: Record<SyncStatus, string> = { offen: 'Offen', laeuft: 'Läuft', fertig: 'Erledigt', fehler: 'Wird wiederholt', tot: 'Aufgegeben' };

/** Arten von Aufträgen – Teil A: Benachrichtigungen aus Business Central; die Teile B–D ergänzen ihre Arten */
export const SYNC_ARTEN = {
  bc_meldung: 'Benachrichtigung aus Business Central',
  artikel: 'Artikelabgleich',
  bestand: 'Bestandsabgleich',
  abos: 'Benachrichtigungen einrichten',
} as const;
export type SyncArt = keyof typeof SYNC_ARTEN;
export const syncArtTitel = (art: string) => (SYNC_ARTEN as Record<string, string>)[art] ?? art;

export interface SyncAuftragZeile {
  id: string;
  art: string;
  titel: string;
  schluessel: string;
  status: SyncStatus;
  versuche: number;
  naechsterVersuch: string | null;
  letzterFehler: string;
  erstelltAm: string;
  geaendertAm: string;
}

export const SYNC_RICHTUNGEN = ['erp_shop', 'shop_erp', 'intern'] as const;
export type SyncRichtung = (typeof SYNC_RICHTUNGEN)[number];
export const SYNC_RICHTUNG_TITEL: Record<SyncRichtung, string> = { erp_shop: 'ERP → Shop', shop_erp: 'Shop → ERP', intern: 'Intern' };

/** probe = im Testmodus nur berechnet, nicht geschrieben */
export const SYNC_ERGEBNISSE = ['ok', 'fehler', 'uebersprungen', 'probe'] as const;
export type SyncErgebnis = (typeof SYNC_ERGEBNISSE)[number];
export const SYNC_ERGEBNIS_TITEL: Record<SyncErgebnis, string> = { ok: 'Geschrieben', fehler: 'Fehler', uebersprungen: 'Unverändert', probe: 'Testmodus' };

export interface SyncProtokollZeile {
  id: string;
  zeit: string;
  richtung: SyncRichtung;
  objektArt: string;
  objektRef: string;
  ergebnis: SyncErgebnis;
  payloadHash: string;
  text: string;
}

export interface ErpListe<T> {
  eintraege: T[];
  /** Cursor für die nächste Seite – null = Ende */
  weiter: string | null;
}

export const ErpAuftraegeAbfrage = z.object({
  status: z.enum(['fehler', 'tot', 'offen']).default('tot'),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export const ErpProtokollAbfrage = z.object({
  ergebnis: z.enum(SYNC_ERGEBNISSE).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export const ErpWiederholenSchema = z.object({
  /** leer = alle aufgegebenen Aufträge */
  ids: z.array(z.string().uuid()).max(500).default([]),
});

export interface ErpZaehler {
  offen: number;
  fehler: number;
  tot: number;
}

/** GET /api/konto/erp – alles für den Reiter „Verbindung“ und die Zähler der Reiter */
export interface ErpUebersicht {
  verbindung: ErpVerbindungAnzeige | null;
  shop: ErpShopStand;
  zaehler: ErpZaehler;
  feldhoheit: ErpFeldhoheit;
  /** Adresse, an die Business Central Benachrichtigungen schickt (je Verbindung) */
  benachrichtigung: string | null;
  /** Teil B: Einstellungen (mit Standardwerten) und Stand der Abgleiche */
  produkte: ErpProduktEinstellungen;
  lager: ErpLagerEinstellungen;
  preise: ErpPreisEinstellungen;
  staende: ErpStromStand[];
  /** Bulk-Erstimporte im Shop (offene zuerst, dann die letzten) */
  bulk: ErpBulkStand[];
  /** Produkte, die noch auf Kanal, Bild oder Freigabe warten */
  nacharbeit: { kanal: number; bild: number; freigabe: number };
}
